const galleryVideos = new WeakMap();

class ZentusRolyVideo extends HTMLElement {
  #controller;
  #observer;
  #visibilityObserver;
  #intersecting = false;
  #state = { failed: false, autoplayBlocked: false, userActivated: false, video: null, owner: null };
  #mediaController;
  #selected = true;
  #video;
  #playPending = false;
  #dialogOpen = false;
  #returnFocus;
  #scrollStyles;
  #pausedByOtherMedia = false;
  #hoverPreview;
  #previewController;

  connectedCallback() {
    const gallery = this.closest('media-gallery');
    const mediaId = this.dataset.mediaId;
    if (gallery && mediaId) {
      let videos = galleryVideos.get(gallery);
      if (!videos) { videos = new Map(); galleryVideos.set(gallery, videos); }
      if (videos.has(mediaId)) this.#state = videos.get(mediaId);
      else videos.set(mediaId, this.#state);
    }
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const openButton = this.querySelector('[data-video-open]');
    const playButton = this.querySelector('[data-video-play]');
    openButton?.addEventListener('click', this.#openDialog, { signal });
    playButton?.addEventListener('click', this.#playInline, { signal });
    this.querySelector('[data-video-close]')?.addEventListener('click', this.#dismissDialog, { signal });
    const dialog = this.querySelector('[data-video-dialog]');
    // Let controls keep their native gesture; inline media still bubbles gallery swipes.
    for (const control of [openButton, playButton, dialog]) {
      for (const type of ['pointerdown', 'mousedown']) {
        control?.addEventListener(type, (event) => event.stopPropagation(), { signal });
      }
    }
    dialog?.addEventListener('close', this.#dismissDialog, { signal });
    dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeDialog();
    }, { signal });
    dialog?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.#closeDialog();
    }, { signal });
    document.addEventListener('visibilitychange', this.#visibilityChanged, { signal });
    document.addEventListener('media:started-playing', this.#otherMedia, { signal });
    document.addEventListener('play', this.#nativeMedia, { signal, capture: true });
    gallery?.addEventListener('slideshow:select', this.#slideSelected, { signal });
    this.#observer = new IntersectionObserver((entries) => {
      this.#intersecting = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0);
      this.#visibilityChanged();
    }, { threshold: [0, 0.25] });
    this.#observer.observe(this);
    this.#visibilityObserver = new MutationObserver(this.#visibilityChanged);
    const slide = this.closest('slideshow-slide');
    if (slide) this.#visibilityObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden', 'hidden'] });
    // A leading clip already on screen need not wait for observer delivery.
    const bounds = this.getBoundingClientRect();
    this.#intersecting = bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.right > 0 &&
      bounds.top < window.innerHeight && bounds.left < window.innerWidth;
    this.#visibilityChanged();
  }

  disconnectedCallback() {
    this.#previewController?.abort();
    this.#releaseVideo();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#visibilityObserver?.disconnect();
  }

  #eligible() {
    const slide = this.closest('slideshow-slide');
    return this.isConnected && this.closest('media-gallery')?.dataset.rolyProductPage === 'true' &&
      !this.closest('dialog, quick-add-dialog, featured-product-information, product-card') &&
      !document.hidden && this.#selected && !slide?.matches('[hidden], [aria-hidden="true"]') &&
      (this.#dialogOpen || (this.getClientRects().length > 0 && !this.closest('[hidden], [aria-hidden="true"]')));
  }

  #canPlay() {
    return this.#eligible() && (this.#dialogOpen || this.#intersecting);
  }

  #visibilityChanged = () => {
    if (!this.#canPlay()) {
      this.pause();
      this.#pausedByOtherMedia = false;
      this.#hoverPreview = undefined;
      this.#setPlayback('hidden');
      return;
    }
    if (this.#state.failed) { this.#showError(); return; }
    // Native controls own playback while the page dialog is open.
    if (this.#dialogOpen) { this.#setPlayback('fullscreen'); return; }
    if (this.#pausedByOtherMedia) { this.#setPlayback('other-media', true); return; }
    if (this.#state.autoplayBlocked) { this.#setPlayback('autoplay-blocked', true); return; }
    const owner = this.#state.owner;
    if (owner && owner !== this && owner.#dialogOpen) return;
    if (!this.#state.userActivated && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this.#setPlayback('reduced-motion', true); return; }
    if (!this.#state.userActivated && navigator.connection?.saveData) { this.#setPlayback('save-data', true); return; }
    this.#play();
  };

  #slideSelected = (event) => {
    const slide = this.closest('slideshow-slide');
    if (!slide || event.target !== slide.closest('slideshow-component')) return;
    const slides = [...event.target.querySelectorAll('slideshow-slide')];
    this.#selected = slides.indexOf(slide) === event.detail.index;
    this.#visibilityChanged();
  };

  #otherMedia = (event) => {
    if (event.detail?.resource !== this && event.detail?.resource !== this.#state.owner) {
      this.#hoverPreview = undefined;
      this.#pausedByOtherMedia = true;
      this.pause();
      this.#setPlayback('other-media', true);
    }
  };

  #nativeMedia = (event) => {
    if (event.target !== this.#state.video) {
      // Only return playback that a temporary card preview interrupted.
      const preview = event.target.closest?.('zentus-card-video');
      // Offscreen or unselected gallery media already follows visibility playback.
      if (preview && !this.#canPlay()) return;
      this.#hoverPreview = preview && (this.#hoverPreview || (this.#video && !this.#video.paused))
        ? event.target : undefined;
      this.#previewController?.abort();
      if (this.#hoverPreview) {
        this.#previewController = new AbortController();
        const { signal } = this.#previewController;
        this.#hoverPreview.addEventListener('pause', this.#previewStopped, { signal });
        this.#hoverPreview.addEventListener('ended', this.#previewStopped, { signal });
      }
      this.#pausedByOtherMedia = true;
      this.pause();
      this.#setPlayback('other-media', true);
    }
  };

  #previewStopped = (event) => {
    if (event.target !== this.#hoverPreview || !event.target.paused) return;
    this.#previewController?.abort();
    this.#hoverPreview = undefined;
    this.#pausedByOtherMedia = false;
    this.#visibilityChanged();
  };

  #load() {
    if (this.#video) return this.#video;
    const video = this.#state.video || this.querySelector(':scope > video') || this.querySelector('template')?.content.querySelector('video')?.cloneNode(true);
    if (!video) return;
    this.#state.owner?.#releaseVideo();
    this.#state.owner = this;
    this.#state.video = video;
    this.#video = video;
    this.#mediaController = new AbortController();
    this.#restoreInline();
    // Metadata can fail before this component adopts the preloaded element.
    if (video.error) {
      this.#state.failed = true;
      this.#showError();
      return;
    }
    const { signal } = this.#mediaController;
    video.addEventListener('play', () => {
      if (!this.#canPlay()) { this.pause(); return; }
      this.dispatchEvent(new CustomEvent('media:started-playing', { bubbles: true, detail: { resource: this } }));
    }, { signal });
    video.addEventListener('playing', () => {
      if (!this.#canPlay()) { this.pause(); return; }
      this.querySelector('img')?.setAttribute('hidden', '');
      this.#setPlayback(this.#dialogOpen ? 'fullscreen' : 'playing');
    }, { signal });
    video.addEventListener('pause', () => {
      if (this.#dialogOpen && this.#playPending) this.#playPending.interrupted = true;
    }, { signal });
    video.addEventListener('error', () => {
      this.#state.failed = true;
      this.pause();
      this.#closeDialog(false);
      this.#showError();
    }, { signal });
    if (video.parentElement !== this) this.appendChild(video);
    return video;
  }

  #play(userGesture = false) {
    if (!this.#canPlay() || this.#state.failed) return;
    const video = this.#load();
    if (!video || (!userGesture && (!video.paused || this.#playPending))) return;
    const attempt = { interrupted: false };
    this.#playPending = attempt;
    this.#setPlayback('loading');
    let interrupted = false;
    let result;
    try { result = video.play(); } catch (error) { result = Promise.reject(error); }
    Promise.resolve(result).then(() => {
      if (this.#video === video && this.#playPending === attempt && !this.#canPlay()) this.pause();
    }).catch((error) => {
      if (this.#video !== video || this.#playPending !== attempt) return;
      if (error?.name === 'AbortError' && attempt.interrupted && video.paused) { interrupted = true; return; }
      // An interrupted load is not an autoplay-policy refusal. A later visibility
      // change may retry it; do not spin an immediate retry loop on other errors.
      this.#state.autoplayBlocked = error?.name === 'NotAllowedError';
      if (this.#dialogOpen) this.#actionFailed();
      else {
        this.querySelector('img')?.removeAttribute('hidden');
        this.#setPlayback(this.#state.autoplayBlocked ? 'autoplay-blocked' : 'play-interrupted', true);
      }
    }).finally(() => {
      if (this.#video !== video || this.#playPending !== attempt) return;
      this.#playPending = false;
      if (interrupted) this.#visibilityChanged();
    });
  }

  #setPlayback(state, offerPlay = false) {
    this.dataset.playbackState = state;
    const button = this.querySelector('[data-video-play]');
    if (button) button.hidden = !offerPlay || !this.#canPlay() || this.#state.failed || this.#dialogOpen;
  }

  #playInline = (event) => {
    event.stopPropagation();
    if (!this.#canPlay() || this.#state.failed || this.#dialogOpen) return;
    this.#state.userActivated = true;
    this.#state.autoplayBlocked = false;
    this.#pausedByOtherMedia = false;
    this.#hoverPreview = undefined;
    this.#restoreInline();
    // Keep play() synchronous with the button gesture; inline playback stays muted.
    this.#play(true);
  };

  #openDialog = (event) => {
    event.stopPropagation();
    if (!this.#eligible() || this.#state.failed || this.#dialogOpen) return;
    const owner = this.#state.owner;
    if (owner && owner !== this && owner.#dialogOpen) return;
    const dialog = this.querySelector('[data-video-dialog]');
    const body = dialog?.querySelector('[data-video-dialog-body]');
    if (!body || typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function') { this.#actionFailed(); return; }
    const video = this.#load();
    if (!video) { if (!this.#state.failed) this.#actionFailed(); return; }
    this.#state.userActivated = true;
    this.#state.autoplayBlocked = false;
    this.#pausedByOtherMedia = false;
    this.#hoverPreview = undefined;
    this.querySelector('[data-video-action-error]')?.setAttribute('hidden', '');
    this.#returnFocus = this.querySelector('[data-video-open]');
    try {
      dialog.showModal();
      this.#dialogOpen = true;
      this.#scrollStyles = [document.documentElement, document.body].map((element) => ({
        element, value: element.style.getPropertyValue('overflow'), priority: element.style.getPropertyPriority('overflow'),
      }));
      this.#scrollStyles.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
      body.appendChild(video);
      video.controls = true;
      video.muted = false;
      // Advisory in supporting browsers; the scoped control CSS covers WebKit.
      try { video.controlsList?.add('nofullscreen'); } catch { /* Other native controls remain available. */ }
      // Keep unmuting and play() synchronous with the deliberate click gesture.
      this.#play(true);
      this.querySelector('[data-video-close]')?.focus({ preventScroll: true });
    } catch {
      this.#actionFailed();
    }
  };

  #dismissDialog = (event) => {
    if (event.type === 'click') event.stopPropagation();
    // close is queued by browsers; ignore a previous close after a new open.
    if (event.type === 'close' && this.querySelector('[data-video-dialog]')?.open) return;
    this.#closeDialog();
  };

  #closeDialog(resume = true) {
    if (!this.#dialogOpen) return;
    this.#dialogOpen = false;
    const dialog = this.querySelector('[data-video-dialog]');
    if (dialog?.open) dialog.close();
    this.#restoreInline();
    if (this.#video) this.appendChild(this.#video);
    this.#scrollStyles?.forEach(({ element, value, priority }) => {
      if (value) element.style.setProperty('overflow', value, priority);
      else element.style.removeProperty('overflow');
    });
    this.#scrollStyles = undefined;
    const trigger = this.#returnFocus;
    this.#returnFocus = undefined;
    const peers = [...(this.closest('media-gallery')?.querySelectorAll('zentus-roly-video') || [])]
      .filter((player) => player instanceof ZentusRolyVideo && player.#state === this.#state);
    if (resume) {
      this.#visibilityChanged();
      peers.forEach((player) => { if (player !== this) player.#visibilityChanged(); });
    }
    const focusTarget = trigger?.isConnected && trigger.getClientRects().length > 0 && !trigger.closest('[hidden], [aria-hidden="true"]')
      ? trigger : peers.find((player) => player.#eligible())?.querySelector('[data-video-open]');
    focusTarget?.focus({ preventScroll: true });
  }

  #restoreInline() {
    if (!this.#video) return;
    this.#video.controls = false;
    this.#video.muted = true;
    this.#video.defaultMuted = true;
    this.#video.playsInline = true;
    this.#video.loop = true;
  }

  #actionFailed() {
    this.#state.autoplayBlocked = true;
    this.pause();
    this.#closeDialog(false);
    this.#restoreInline();
    this.querySelector('img')?.removeAttribute('hidden');
    this.querySelector('[data-video-action-error]')?.removeAttribute('hidden');
    this.#setPlayback('action-failed', true);
  }

  #showError() {
    this.#setPlayback('media-error');
    if (this.#video) this.#video.hidden = true;
    this.querySelector('img')?.removeAttribute('hidden');
    this.querySelector('[data-video-action-error]')?.setAttribute('hidden', '');
    this.querySelector('[data-video-error]')?.removeAttribute('hidden');
    const button = this.querySelector('[data-video-open]');
    if (button) button.hidden = true;
  }

  #releaseVideo() {
    this.pause();
    this.#closeDialog(false);
    this.#mediaController?.abort();
    this.#restoreInline();
    this.#video?.remove();
    this.#video = undefined;
    this.#playPending = false;
    if (this.#state.owner === this) this.#state.owner = null;
    this.querySelector('img')?.removeAttribute('hidden');
  }

  pause() {
    if (this.#video && !this.#video.paused) {
      if (this.#playPending) this.#playPending.interrupted = true;
      this.#video.pause();
    }
  }
}

if (!customElements.get('zentus-roly-video')) customElements.define('zentus-roly-video', ZentusRolyVideo);
